import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { SectionData } from '@/components/sections/SectionRenderer';
import { BUILT_IN_PAGE_EXTRA_TYPES } from '@/components/sections/section-types';

/** Built-in pages that can carry optional page-builder blocks (page_sections.page_slug). */
export type BuiltInPageSlug = 'home' | 'about' | 'services';

/**
 * Optional page-builder blocks an owner has added to a built-in page (home,
 * about, services). They live in `page_sections` keyed by `page_slug` instead
 * of `page_id` — the column the table always reserved for built-in pages.
 */
export function usePageExtraSections(orgId: string | undefined, pageSlug: BuiltInPageSlug) {
  return useQuery({
    queryKey: ['page-extra-sections', orgId, pageSlug],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<SectionData[]> => {
      const { data, error } = await supabase
        .from('page_sections')
        .select('id, section_type, content, layout_variant, is_visible, sort_order')
        .eq('organization_id', orgId!)
        .eq('page_slug', pageSlug)
        // Only optional blocks. provision_org seeds legacy ('home','hero') and
        // ('home','services') rows that must never render as extra sections.
        .in('section_type', [...BUILT_IN_PAGE_EXTRA_TYPES])
        .eq('is_visible', true)
        .order('sort_order');

      if (error) throw error;

      return (data ?? []).map((section) => ({
        ...section,
        content: (section.content || {}) as Record<string, unknown>,
        layout_variant: section.layout_variant || 'default',
      }));
    },
  });
}
